if (!customElements.get('product-form')) {
  class ProductForm extends window.BaseComponent {
    constructor() {
      super();
      this._onSubmit = this.onSubmitHandler.bind(this);
      this._submitting = false;
    }

    init() {
      this.form = this.querySelector('form');
      // Remove any previous listener before adding (prevents duplicates on re-init)
      this.form.removeEventListener('submit', this._onSubmit);
      this.form.addEventListener('submit', this._onSubmit);
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this._submitting) return;

      const submitButton = this.querySelector('[name="add"]');
      if (!submitButton || submitButton.classList.contains('loading')) return;

      this._submitting = true;
      submitButton.setAttribute('disabled', true);
      submitButton.classList.add('loading');

      const spinner = this.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.remove('hidden');

      const config = {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new FormData(this.form)
      };

      fetch(`${window.routes.cart_add_url}.js`, config)
        .then((response) => {
          if (response.status === 429) {
            throw { type: 'rate-limit', status: 429 };
          }
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          return response.json();
        })
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);
            return;
          }

          // Confirmation: button label + toast
          this.showAddedState(submitButton);
          this.showAddedToast(response);

          // Success — fetch cart totals for header count update.
          // Delay slightly to let Shopify's dynamic checkout SDK finish
          // its own /cart.js refetch first, avoiding 429 rate limits.
          if (window.pubsub) {
            this._fetchCartForPubsub();
          }
        })
        .catch((e) => {
          if (e && e.type === 'rate-limit') {
            console.warn('Add to cart rate-limited. Retrying in 2s...');
            setTimeout(() => {
              this._submitting = false;
              submitButton.removeAttribute('disabled');
              submitButton.classList.remove('loading');
              if (spinner) spinner.classList.add('hidden');
              // Re-submit automatically
              this.onSubmitHandler(evt);
            }, 2000);
            return;
          }
          console.error('Add to cart error:', e);
        })
        .finally(() => {
          this._submitting = false;
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        });
    }

    /* Button label shows "ADDED ✓" for 2.5s, then returns to its original text */
    showAddedState(submitButton) {
      const label = submitButton.querySelector('[data-atc-text]');
      if (!label) return;

      const addedText = 'ADDED ✓';
      if (label.textContent.trim() !== addedText) {
        label.dataset.originalText = label.textContent;
      }
      label.textContent = addedText;

      clearTimeout(this._addedTimer);
      this._addedTimer = setTimeout(() => {
        // Only restore if nothing else (like a size change) replaced the label meanwhile
        if (label.textContent.trim() === addedText && label.dataset.originalText) {
          label.textContent = label.dataset.originalText;
        }
      }, 2500);
    }

    /* Toast: "{Product} ({Size}) added to your cart" with VIEW CART, auto hides after 5s */
    showAddedToast(item) {
      ProductForm.injectToastStyles();

      let toast = document.getElementById('AtcToast');
      if (!toast) {
        toast = document.createElement('div');
        toast.id = 'AtcToast';
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');

        const msg = document.createElement('span');
        msg.className = 'atc-toast__msg';

        const link = document.createElement('a');
        link.className = 'atc-toast__link';
        link.href = (window.routes && window.routes.cart_url) || '/cart';
        link.textContent = 'VIEW CART';

        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'atc-toast__close';
        close.setAttribute('aria-label', 'Close');
        close.textContent = '×';

        toast.append(msg, link, close);
        document.body.appendChild(toast);

        const hide = () => {
          clearTimeout(window.__atcToastTimer);
          toast.classList.remove('is-visible');
        };
        close.addEventListener('click', hide);
        link.addEventListener('click', hide);
      }

      const title = (item && item.product_title) || 'Item';
      const variant = item && item.variant_title && item.variant_title !== 'Default Title' ? ` (${item.variant_title})` : '';
      toast.querySelector('.atc-toast__msg').textContent = `${title}${variant} added to your cart`;

      // Restart the slide up if it's already showing
      toast.classList.remove('is-visible');
      void toast.offsetWidth;
      toast.classList.add('is-visible');

      clearTimeout(window.__atcToastTimer);
      window.__atcToastTimer = setTimeout(() => {
        toast.classList.remove('is-visible');
      }, 5000);
    }

    static injectToastStyles() {
      if (document.getElementById('AtcToastStyles')) return;
      const style = document.createElement('style');
      style.id = 'AtcToastStyles';
      style.textContent = `
        #AtcToast {
          position: fixed;
          left: 16px;
          right: 16px;
          bottom: calc(var(--footer-height, 0px) + 16px);
          margin: 0 auto;
          max-width: 440px;
          box-sizing: border-box;
          z-index: 2147483000;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 16px;
          background: rgb(226, 253, 220);
          border: 1px solid rgb(51, 51, 51);
          color: rgb(51, 51, 51);
          font-family: "Courier New", Courier, monospace;
          font-size: 13px;
          line-height: 1.4;
          letter-spacing: 0.02em;
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.3s ease, transform 0.3s ease;
          pointer-events: none;
        }
        #AtcToast.is-visible {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }
        #AtcToast .atc-toast__msg {
          flex: 1;
          min-width: 0;
        }
        #AtcToast .atc-toast__link {
          color: inherit;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-decoration: underline;
          white-space: nowrap;
        }
        #AtcToast .atc-toast__close {
          background: none;
          border: 0;
          padding: 0 2px;
          margin: 0;
          cursor: pointer;
          color: inherit;
          font-family: inherit;
          font-size: 20px;
          line-height: 1;
        }
        @media (prefers-reduced-motion: reduce) {
          #AtcToast { transition: none; transform: none; }
        }
      `;
      document.head.appendChild(style);
    }

    _fetchCartForPubsub(retryCount) {
      retryCount = retryCount || 0;

      // Wait 1.5s before fetching to let Shopify's checkout SDK settle
      setTimeout(() => {
        fetch(`${window.routes.cart_url}.js`)
          .then(res => {
            if (res.status === 429) {
              throw { type: 'rate-limit' };
            }
            return res.json();
          })
          .then(cart => {
            window.pubsub.publish('cart-update', cart);
          })
          .catch(err => {
            if (err && err.type === 'rate-limit' && retryCount < 2) {
              const backoff = Math.pow(2, retryCount + 1) * 1000;
              console.warn(`Cart fetch rate-limited. Retry ${retryCount + 1}/2 in ${backoff / 1000}s...`);
              setTimeout(() => this._fetchCartForPubsub(retryCount + 1), backoff);
            }
          });
      }, 1500);
    }

    handleErrorMessage(errorMessage) {
      console.error(errorMessage);
      alert(errorMessage);
    }
  }

  if (window.BaseComponent) {
    customElements.define('product-form', ProductForm);
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      if (window.BaseComponent) customElements.define('product-form', ProductForm);
    });
  }
}